import { Button, Container, Element, Label } from '@playcanvas/pcui';

import { AnnotationList } from './annotation-list';
import { Events } from '../events';
import { localize } from './localization';
import { SplatList } from './splat-list';
import sceneImportSvg from './svg/import.svg';
import sceneNewSvg from './svg/new.svg';
import soloSvg from './svg/solo.svg';
import { Tooltips } from './tooltips';
import { Transform } from './transform';

const createSvg = (svgString: string) => {
    const decodedStr = decodeURIComponent(svgString.substring('data:image/svg+xml,'.length));
    return new DOMParser().parseFromString(decodedStr, 'image/svg+xml').documentElement;
};

class ScenePanel extends Container {
    constructor(events: Events, tooltips: Tooltips, args = {}) {
        args = {
            ...args,
            id: 'scene-panel',
            class: 'panel'
        };

        super(args);

        // stop pointer events bubbling
        ['pointerdown', 'pointerup', 'pointermove', 'wheel', 'dblclick'].forEach((eventName) => {
            this.dom.addEventListener(eventName, (event: Event) => event.stopPropagation());
        });

        const sceneHeader = new Container({
            class: 'panel-header'
        });

        const sceneIcon = new Label({
            text: '\uE344',
            class: 'panel-header-icon'
        });

        const sceneLabel = new Label({
            text: localize('panel.scene-manager'),
            class: 'panel-header-label'
        });

        let soloActive = false;

        const soloToggle = new Container({
            class: 'panel-header-button'
        });
        soloToggle.dom.appendChild(createSvg(soloSvg));

        soloToggle.on('click', () => {
            soloActive = !soloActive;
            if (soloActive) {
                soloToggle.class.add('active');
            } else {
                soloToggle.class.remove('active');
            }
            events.fire('scene.solo', soloActive);
        });

        const sceneImport = new Container({
            class: 'panel-header-button'
        });
        sceneImport.dom.appendChild(createSvg(sceneImportSvg));

        const sceneNew = new Container({
            class: 'panel-header-button'
        });
        sceneNew.dom.appendChild(createSvg(sceneNewSvg));

        sceneHeader.append(sceneIcon);
        sceneHeader.append(sceneLabel);
        sceneHeader.append(soloToggle);
        sceneHeader.append(sceneImport);
        sceneHeader.append(sceneNew);

        sceneImport.on('click', async () => {
            await events.invoke('scene.import');
        });

        sceneNew.on('click', () => {
            events.invoke('doc.new');
        });

        tooltips.register(soloToggle, localize('tooltip.scene.solo'), 'top');
        tooltips.register(sceneImport, 'Import Scene', 'top');
        tooltips.register(sceneNew, 'New Scene', 'top');

        const splatList = new SplatList(events);

        const splatListContainer = new Container({
            class: 'splat-list-container'
        });
        splatListContainer.append(splatList);

        const transformHeader = new Container({
            class: 'panel-header'
        });

        const transformIcon = new Label({
            text: '\uE111',
            class: 'panel-header-icon'
        });

        const transformLabel = new Label({
            text: localize('panel.scene-manager.transform'),
            class: 'panel-header-label'
        });

        transformHeader.append(transformIcon);
        transformHeader.append(transformLabel);

        // annotations section

        const annotationHeader = new Container({
            class: 'panel-header'
        });

        const annotationIcon = new Label({
            text: '',
            class: 'panel-header-icon'
        });

        const annotationLabel = new Label({
            text: 'ANNOTATIONS',
            class: 'panel-header-label'
        });

        const annotationAdd = new Container({
            class: 'panel-header-button'
        });
        annotationAdd.dom.appendChild(createSvg(sceneNewSvg));

        annotationAdd.on('click', () => {
            if (events.invoke('annotations.placing')) {
                events.fire('annotations.stopPlacing');
            } else {
                events.fire('annotations.startPlacing');
            }
        });

        // reflect placement mode on the add button
        events.on('annotations.placingChanged', (active: boolean) => {
            if (active) {
                annotationAdd.class.add('active');
            } else {
                annotationAdd.class.remove('active');
            }
        });

        annotationHeader.append(annotationIcon);
        annotationHeader.append(annotationLabel);
        annotationHeader.append(annotationAdd);

        tooltips.register(annotationAdd, 'Add Annotation (click in viewport)', 'top');

        const annotationListContainer = new Container({
            class: 'splat-list-container'
        });
        annotationListContainer.append(new AnnotationList(events));

        // add-content section

        const contentHeader = new Container({
            class: 'panel-header'
        });

        const contentIcon = new Label({
            text: '',
            class: 'panel-header-icon'
        });

        const contentLabel = new Label({
            text: 'ADD CONTENT',
            class: 'panel-header-label'
        });

        contentHeader.append(contentIcon);
        contentHeader.append(contentLabel);

        const contentButtons = new Container({
            class: 'content-buttons'
        });

        const addButton = (text: string, onClick: () => void, dragPayload?: Record<string, unknown>) => {
            const btn = new Button({ class: 'content-button', text });
            btn.on('click', onClick);
            if (dragPayload) {
                btn.dom.draggable = true;
                btn.dom.setAttribute('data-content-drag', JSON.stringify(dragPayload));
                btn.dom.title = `${text} — click to add, or drag into the viewport`;
            }
            contentButtons.append(btn);
        };

        // image: open a file picker and add as a textured plane
        const pickImage = () => {
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = 'image/*';
            input.onchange = () => {
                const file = input.files?.[0];
                if (!file) {
                    return;
                }
                const reader = new FileReader();
                reader.onload = () => events.fire('content.addImage', reader.result as string);
                reader.readAsDataURL(file);
            };
            input.click();
        };

        const addText = () => {
            // eslint-disable-next-line no-alert
            const text = window.prompt('Label text:', 'Text');
            if (text !== null) {
                events.fire('content.addText', text);
            }
        };

        addButton('Image', pickImage);
        addButton('Text', addText, { kind: 'text', text: 'Text' });
        addButton('Box', () => events.fire('content.addShape', 'box'), { kind: 'shape', shape: 'box' });
        addButton('Sphere', () => events.fire('content.addShape', 'sphere'), { kind: 'shape', shape: 'sphere' });
        addButton('Plane', () => events.fire('content.addShape', 'plane'), { kind: 'shape', shape: 'plane' });
        addButton('Water', () => events.fire('content.addWater'), { kind: 'water' });

        this.append(sceneHeader);
        this.append(splatListContainer);
        this.append(transformHeader);
        this.append(new Transform(events));
        this.append(annotationHeader);
        this.append(annotationListContainer);
        this.append(contentHeader);
        this.append(contentButtons);
        this.append(new Element({
            class: 'panel-header',
            height: 20
        }));
    }
}

export { ScenePanel };
